/**
 * Хук со всей бизнес-логикой страницы Git.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { globalSnackbar } from 'components/GlobalSnackbar';
import indexedDBStorage from 'utils/indexedDBStorage';
import { safeParseApiError, getErrorMessage } from 'utils/parseApiError';
import {
  setIsGitLoading,
  setGitTaskId,
  setGitProgress,
  incrementGitContextVersion,
} from '../../../redux/reducers';
import { restoreGitIndexingState, clearGitIndexingState } from 'events/webSocketHandlers';
import {
  downloadRepository,
  fetchBranches,
  fetchFileContent as apiFetchFileContent,
  getIndexStatus,
  saveToContext,
  stopIndexing,
} from '../api/gitApi';
import { startStatusCheck, stopStatusCheck } from '../api/statusCheck';
import {
  GIT_TOKEN_KEY,
  GIT_URL_KEY,
  GIT_BRANCH_KEY,
  GIT_BRANCHES_CACHE_KEY,
  GIT_SELECTED_FILES_KEY as FILES_KEY,
  DEFAULT_REPO_URL,
} from '../constants';
import {
  buildFullPath,
  getLanguageForFile,
  traverseForChosenFiles,
  getExistingFileKeys,
} from '../utils/fileUtils';
import type {
  FileTreeNode,
  GitBranch,
  GitBranchRaw,
  GitFileRef,
  SelectedFilesState,
} from '../types';

interface RootState {
  reducer: {
    project: { id?: string };
    isGitLoading: boolean;
    gitProgress: { value: number | null; label: string | null };
    gitTaskId: string | null;
  };
}

function formatBranch(branch: GitBranchRaw): GitBranch {
  return {
    name: branch.name,
    isDefault: branch.name === 'main' || branch.name === 'master',
    lastUpdate: branch.last_update,
  };
}

/**
 * Преобразует техническую ошибку Git/GitLab в понятное пользователю сообщение.
 * Бэкенд возвращает сырой текст вида
 * `Gitlab вернул HTTP статус 404: ... {"message":"404 Project Not Found"}`.
 */
function humanizeGitError(err: unknown, fallback: string): string {
  const haystack =
    `${safeParseApiError(err).generalMessage} ${getErrorMessage(err, '')}`.toLowerCase();

  if (haystack.includes('404') || haystack.includes('not found')) {
    return 'Репозиторий не найден. Проверьте правильность ссылки на репозиторий.';
  }
  if (/40[13]|unauthorized|forbidden|access denied|invalid token/.test(haystack)) {
    return 'Нет доступа к репозиторию. Проверьте токен и права доступа.';
  }
  return fallback;
}

export function useGitPage() {
  const currentProject = useSelector((state: RootState) => state.reducer.project);
  const isGitLoading = useSelector((state: RootState) => state.reducer.isGitLoading);
  const gitProgress = useSelector((state: RootState) => state.reducer.gitProgress);
  const gitTaskId = useSelector((state: RootState) => state.reducer.gitTaskId);
  const dispatch = useDispatch();
  const isFirstRender = useRef(true);
  const fileContentCacheRef = useRef<Record<string, string>>({});

  // Auth
  const [url, setUrl] = useState(() => {
    try {
      const saved = localStorage.getItem(GIT_URL_KEY);
      if (saved?.trim()) return saved.trim();
    } catch (_) {
      /* ignore */
    }
    return DEFAULT_REPO_URL;
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [savedAccessToken, setSavedAccessToken] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Repo
  const [loading, setLoading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [fileTree, setFileTree] = useState<FileTreeNode[]>([]);
  const [branches, setBranches] = useState<GitBranch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState(() => {
    try {
      const saved = localStorage.getItem(GIT_BRANCH_KEY);
      if (saved?.trim()) return saved.trim();
    } catch (_) {
      /* ignore */
    }
    return '';
  });
  const [isEditingUrl, setIsEditingUrl] = useState(false);
  const [originalUrl, setOriginalUrl] = useState('');

  // Files
  const [selectedFiles, setSelectedFiles] = useState<SelectedFilesState>({
    url: '',
    files: [],
  });
  const [selectedFile, setSelectedFile] = useState<GitFileRef | null>(null);
  const [selectedFileContent, setSelectedFileContent] = useState<string | null>(null);
  const [loadingFileContent, setLoadingFileContent] = useState(false);

  // Tree expand state
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  // --- Init from localStorage ---
  useEffect(() => {
    try {
      const saved = localStorage.getItem(GIT_TOKEN_KEY);
      if (saved?.trim()) {
        setSavedAccessToken(saved.trim());
        setIsAuthenticated(true);
      }
    } catch (_) {
      /* ignore */
    }

    return () => {
      stopStatusCheck();
      delete (window as { updateGitComponent?: unknown }).updateGitComponent;
    };
  }, [currentProject?.id]);

  // --- Restore from IndexedDB ---
  useEffect(() => {
    const load = async () => {
      try {
        const saved = (await indexedDBStorage.getItem(FILES_KEY)) as {
          fileTree?: FileTreeNode[];
          files?: GitFileRef[];
          url?: string;
          branch?: string;
          expandedPaths?: string[];
          selectedFile?: GitFileRef;
        } | null;
        if (!saved) return;
        if (Array.isArray(saved.fileTree) && saved.fileTree.length > 0) {
          setFileTree(saved.fileTree);
        }
        if (Array.isArray(saved.files) && saved.files.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          setSelectedFiles((prev) => ({ ...prev, files: saved.files! }));
        }
        if (saved.url) setUrl(saved.url);
        if (saved.branch) setSelectedBranch(saved.branch);
        if (Array.isArray(saved.expandedPaths) && saved.expandedPaths.length > 0) {
          setExpandedPaths(new Set(saved.expandedPaths));
        }
        if (saved.selectedFile) {
          setSelectedFile(saved.selectedFile);
        }
      } catch (e) {
        console.error('Ошибка восстановления выбранных файлов:', e);
      }
    };
    load();
  }, []);

  // --- Persist to IndexedDB ---
  useEffect(() => {
    const save = async () => {
      try {
        await indexedDBStorage.setItem(FILES_KEY, {
          url,
          branch: selectedBranch,
          files: selectedFiles.files,
          fileTree,
          expandedPaths: Array.from(expandedPaths),
          selectedFile,
        });
      } catch (e) {
        console.error('Ошибка сохранения выбранных файлов:', e);
      }
    };
    save();
  }, [
    currentProject?.id,
    url,
    selectedBranch,
    selectedFiles.files,
    fileTree,
    expandedPaths,
    selectedFile,
  ]);

  // --- Sync chosen files from server response ---
  useEffect(() => {
    if (!Array.isArray(fileTree) || fileTree.length === 0) return;
    const chosenFiles = traverseForChosenFiles(fileTree, '');
    if (chosenFiles.length === 0) return;
    setSelectedFiles((prev) => {
      const merged = [...prev.files];
      chosenFiles.forEach((cf) => {
        if (!merged.some((f) => f.path === cf.path && f.name === cf.name)) {
          merged.push(cf);
        }
      });
      return { ...prev, files: merged };
    });
  }, [fileTree]);

  // --- Filter selected files by existing in tree ---
  useEffect(() => {
    if (!Array.isArray(fileTree) || fileTree.length === 0) return;
    const existingKeys = getExistingFileKeys(fileTree, '');
    setSelectedFiles((prev) => {
      if (!Array.isArray(prev.files) || prev.files.length === 0) return prev;
      const filtered = prev.files.filter((file) => existingKeys.has(`${file.path}|${file.name}`));
      return filtered.length !== prev.files.length ? { ...prev, files: filtered } : prev;
    });
  }, [fileTree, selectedBranch]);

  // --- Restore indexing state ---
  useEffect(() => {
    if (!currentProject?.id) return;
    const savedState = restoreGitIndexingState(currentProject.id);
    if (savedState?.isLoading) {
      dispatch(setGitTaskId(savedState.taskId));
      dispatch(setIsGitLoading(true));
      dispatch(setGitProgress(savedState.progress));
    }
  }, [currentProject?.id, dispatch]);

  // --- Однократная проверка статуса индексации при наличии git_indexing_state ---
  useEffect(() => {
    if (!gitTaskId || !isGitLoading) return;
    let cancelled = false;
    getIndexStatus(gitTaskId)
      .then((response) => {
        if (cancelled) return;
        const { status, progress } = response;
        if (status === 'done') {
          globalSnackbar({
            message: 'Индексация репозитория успешно завершена',
            severity: 'success',
            autoClose: false,
          });
          clearGitIndexingState();
          dispatch(setIsGitLoading(false));
          dispatch(setGitTaskId(null));
          dispatch(setGitProgress({ value: 0, label: '' }));
          dispatch(incrementGitContextVersion());
        } else if (status === 'failed' || status === 'failded') {
          globalSnackbar({
            message: 'Во время индексации репозитория возникли проблемы',
            severity: 'error',
            autoClose: false,
          });
          clearGitIndexingState();
          dispatch(setIsGitLoading(false));
          dispatch(setGitTaskId(null));
          dispatch(setGitProgress({ value: 0, label: '' }));
        } else if (status === 'processing' || status === 'pending') {
          if (progress) {
            dispatch(
              setGitProgress({ value: progress.value, label: `${progress.label}\u00A0\u00A0` })
            );
            try {
              const savedStateStr = localStorage.getItem('git_indexing_state');
              if (savedStateStr) {
                const savedState = JSON.parse(savedStateStr);
                localStorage.setItem(
                  'git_indexing_state',
                  JSON.stringify({
                    ...savedState,
                    progress: { value: progress.value, label: `${progress.label}\u00A0\u00A0` },
                    timestamp: Date.now(),
                  })
                );
              }
            } catch (_) {
              /* ignore */
            }
          }
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('[Git] Ошибка проверки статуса индексации:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [gitTaskId, isGitLoading, dispatch]);

  // --- Clear indexing on project change ---
  useEffect(() => {
    if (!currentProject?.id || currentProject.id === 'undefined') return;
    const prevProjectId = localStorage.getItem('prev_project_id_for_git');
    const currentProjectIdStr = String(currentProject.id);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      localStorage.setItem('prev_project_id_for_git', currentProjectIdStr);
      return;
    }

    if (prevProjectId && prevProjectId !== 'undefined' && prevProjectId !== currentProjectIdStr) {
      clearGitIndexingState();
      dispatch(setIsGitLoading(false));
      dispatch(setGitTaskId(null));
      dispatch(setGitProgress({ value: 0, label: '' }));
    }
    localStorage.setItem('prev_project_id_for_git', currentProjectIdStr);
  }, [currentProject?.id, dispatch]);

  // --- Branches cache from IndexedDB ---
  useEffect(() => {
    const load = async () => {
      try {
        const parsed = (await indexedDBStorage.getItem(GIT_BRANCHES_CACHE_KEY)) as {
          project_id?: string;
          url?: string;
          branches?: GitBranch[];
        } | null;
        if (!parsed || parsed.project_id !== currentProject?.id || parsed.url !== url) return;
        if (!Array.isArray(parsed.branches)) return;
        setBranches(parsed.branches);
        const names = parsed.branches.map((b) => b.name);
        if (selectedBranch && !names.includes(selectedBranch)) {
          const fallback =
            parsed.branches.find((b) => b.isDefault)?.name ?? parsed.branches[0]?.name ?? '';
          if (fallback) setSelectedBranch(fallback);
        }
      } catch (_) {
        /* ignore */
      }
    };
    load();
  }, [currentProject?.id, url, selectedBranch]);

  // --- Handlers ---
  const fetchFileContent = useCallback(
    async (path: string, name: string): Promise<string | null> => {
      const fullPath = buildFullPath(path, name);
      const cache = fileContentCacheRef.current;
      if (cache[fullPath] !== undefined) return cache[fullPath];
      const projectId = currentProject?.id;
      if (!projectId || !url || !selectedBranch) return null;
      // Без авторизации в Git запрос за содержимым файла не имеет смысла:
      // восстановленный из IndexedDB selectedFile не должен дёргать git/file.
      if (!isAuthenticated || !savedAccessToken?.trim()) return null;
      try {
        const content = await apiFetchFileContent(projectId, url, selectedBranch, fullPath);
        cache[fullPath] = content;
        return content;
      } catch (err) {
        console.error('Ошибка загрузки файла:', err);
        globalSnackbar({
          message: `Ошибка загрузки файла: ${(err as Error).message}`,
          severity: 'error',
        });
        return null;
      }
    },
    [currentProject?.id, url, selectedBranch, isAuthenticated, savedAccessToken]
  );

  const handleLoadCode = useCallback(() => {
    if (!isAuthenticated) {
      globalSnackbar({ message: 'Сначала авторизуйтесь в Git', severity: 'warning' });
      setLoginModalOpen(true);
      return;
    }
    const missing: string[] = [];
    if (!url?.trim()) missing.push('URL репозитория');
    if (!savedAccessToken?.trim()) missing.push('Access Token');
    if (missing.length > 0) {
      globalSnackbar({ message: `Заполните: ${missing.join(', ')}`, severity: 'error' });
      return;
    }

    // Сбрасываем дерево и выбор: загруженные ветки могут относиться к другому
    // репозиторию, поэтому старое дерево файлов больше не актуально.
    setFileTree([]);
    setSelectedFiles({ url: '', files: [] });
    setSelectedFile(null);
    setSelectedFileContent(null);
    setExpandedPaths(new Set());
    fileContentCacheRef.current = {};

    setLoading(true);
    const requestData = {
      branch: 'master',
      url: url.trim(),
      token: savedAccessToken.trim(),
      project_id: currentProject?.id ?? '',
    };

    downloadRepository(requestData)
      .then((data) => {
        setLoading(false);
        if (data?.taskId) {
          localStorage.setItem('gitTaskId', data.taskId);
          fetchBranches(requestData)
            .then((raw) => {
              const formatted = raw.map(formatBranch);
              setBranches(formatted);
              const defaultBranch = formatted.find((b) => b.isDefault);
              if (defaultBranch && defaultBranch.name !== selectedBranch) {
                setSelectedBranch(defaultBranch.name);
                try {
                  localStorage.setItem(GIT_BRANCH_KEY, defaultBranch.name);
                } catch (_) {
                  /* ignore */
                }
              }
              indexedDBStorage
                .setItem(GIT_BRANCHES_CACHE_KEY, {
                  project_id: currentProject?.id ?? '',
                  url: requestData.url,
                  branches: formatted,
                })
                .catch(() => {
                  /* ignore */
                });
              globalSnackbar({
                message: `Загружено ${formatted.length} веток`,
                severity: 'success',
              });
            })
            .catch((err) => {
              console.error('Ошибка загрузки веток:', err);
              globalSnackbar({
                message: humanizeGitError(err, 'Не удалось загрузить ветки репозитория'),
                severity: 'error',
              });
            });
          globalSnackbar({
            message: `Загрузка веток начата. Task ID: ${data.taskId}`,
            severity: 'info',
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        globalSnackbar({
          message: humanizeGitError(err, 'Не удалось загрузить репозиторий'),
          severity: 'error',
        });
      });
  }, [isAuthenticated, url, savedAccessToken, currentProject?.id, selectedBranch]);

  const handleLoadFiles = useCallback(() => {
    if (!isAuthenticated) {
      globalSnackbar({ message: 'Сначала авторизуйтесь в Git', severity: 'warning' });
      setLoginModalOpen(true);
      return;
    }
    const missing: string[] = [];
    if (!url?.trim()) missing.push('URL репозитория');
    if (!savedAccessToken?.trim()) missing.push('Access Token');
    if (!selectedBranch?.trim()) missing.push('Ветка');
    if (missing.length > 0) {
      globalSnackbar({ message: `Заполните: ${missing.join(', ')}`, severity: 'error' });
      return;
    }

    setLoading(true);
    setLoadingFiles(true);
    setSelectedFile(null);
    setSelectedFileContent(null);
    fileContentCacheRef.current = {};

    const requestData = {
      url: url.trim(),
      token: savedAccessToken.trim(),
      branch: selectedBranch.trim(),
      project_id: currentProject?.id ?? '',
    };

    downloadRepository(requestData)
      .then((data) => {
        if (data?.taskId) {
          localStorage.setItem('gitTaskId', data.taskId);
          globalSnackbar({
            message: `Загрузка файлов начата. Task ID: ${data.taskId}`,
            severity: 'info',
          });
          startStatusCheck(data.taskId, {
            onComplete: () => {
              setLoading(false);
              setLoadingFiles(false);
              globalSnackbar({
                message: 'Файлы успешно загружены',
                severity: 'success',
              });
            },
            setFileTree,
            requestData,
          });
        } else {
          setLoading(false);
          setLoadingFiles(false);
          globalSnackbar({ message: 'Ошибка: не получен Task ID', severity: 'error' });
        }
      })
      .catch((err) => {
        setLoading(false);
        setLoadingFiles(false);
        globalSnackbar({
          message: `Ошибка загрузки файлов: ${(err as Error).message}`,
          severity: 'error',
        });
      });
  }, [isAuthenticated, url, savedAccessToken, selectedBranch, currentProject?.id]);

  const toggleExpandPath = useCallback((path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);

  // Restore selected file content on mount
  useEffect(() => {
    if (!selectedFile || selectedFileContent !== null) return;
    fetchFileContent(selectedFile.path, selectedFile.name).then((content) => {
      if (content !== null) {
        setSelectedFileContent(content);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile?.path, selectedFile?.name]);

  const handleFileSelect = useCallback(
    (path: string, name: string) => {
      setSelectedFile({ path, name });
      const fullPath = buildFullPath(path, name);
      const cached = fileContentCacheRef.current[fullPath];
      if (cached !== undefined) {
        setSelectedFileContent(cached);
        return;
      }
      setLoadingFileContent(true);
      setSelectedFileContent(null);
      fetchFileContent(path, name).then((content) => {
        setSelectedFileContent(content ?? '');
        setLoadingFileContent(false);
      });
    },
    [fetchFileContent]
  );

  const handleFileCheckboxSelect = useCallback(
    (path: string, name: string) => {
      const fullPath = buildFullPath(path, name);
      if (fileContentCacheRef.current[fullPath] !== undefined) return;
      fetchFileContent(path, name);
    },
    [fetchFileContent]
  );

  const handleLogin = useCallback(() => {
    if (!accessToken?.trim()) {
      globalSnackbar({ message: 'Пожалуйста, введите Access Token', severity: 'error' });
      return;
    }
    const token = accessToken.trim();
    setIsAuthenticated(true);
    setSavedAccessToken(token);
    try {
      localStorage.setItem(GIT_TOKEN_KEY, token);
    } catch (_) {
      /* ignore */
    }
    setLoginModalOpen(false);
    setAccessToken('');
    globalSnackbar({ message: 'Успешно авторизован в Git', severity: 'success' });
  }, [accessToken]);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setSavedAccessToken('');
    setLoginModalOpen(false);
    try {
      localStorage.removeItem(GIT_TOKEN_KEY);
    } catch (_) {
      /* ignore */
    }
    globalSnackbar({ message: 'Вы вышли из аккаунта Git', severity: 'info' });
  }, []);

  const handleCloseModal = useCallback(() => {
    setLoginModalOpen(false);
    setAccessToken('');
  }, []);

  const handleCancelLoadFiles = useCallback(() => {
    stopStatusCheck();
    setLoadingFiles(false);
    setLoading(false);
    globalSnackbar({ message: 'Загрузка файлов отменена', severity: 'info' });
  }, []);

  const handleCancelIndex = useCallback(() => {
    stopIndexing(gitTaskId).then(() => {
      dispatch(setIsGitLoading(false));
      dispatch(setGitTaskId(null));
      dispatch(setGitProgress({ value: null, label: null }));
      clearGitIndexingState();
    });
  }, [gitTaskId, dispatch]);

  const handleSaveToContext = useCallback(() => {
    if (selectedFiles.files.length === 0) {
      globalSnackbar({
        message: 'Выберите файлы для сохранения в контекст',
        severity: 'warning',
      });
      return;
    }
    dispatch(setIsGitLoading(true));
    const requestData = {
      url,
      files: selectedFiles.files,
      project_id: currentProject?.id ?? '',
    };
    saveToContext(requestData)
      .then(async (response) => {
        if (response?.task_id) {
          dispatch(setGitTaskId(response.task_id));
          try {
            localStorage.setItem(
              'git_indexing_state',
              JSON.stringify({
                projectId: currentProject?.id,
                taskId: response.task_id,
                isLoading: true,
                progress: { value: 0, label: 'Начало индексации...' },
                timestamp: Date.now(),
              })
            );
          } catch (_) {
            /* ignore */
          }
          await indexedDBStorage.setItem(FILES_KEY, {
            url,
            branch: selectedBranch,
            files: selectedFiles.files,
            fileTree,
          });
          globalSnackbar({
            message: `Сохранено ${selectedFiles.files.length} файлов в контекст. Идет индексация`,
            severity: 'success',
          });
        }
      })
      .catch((err) => {
        globalSnackbar({
          message: `Ошибка сохранения в контекст: ${(err as Error).message}`,
          severity: 'error',
        });
        dispatch(setIsGitLoading(false));
        dispatch(setGitTaskId(null));
      });
  }, [selectedFiles.files, url, currentProject?.id, selectedBranch, fileTree, dispatch]);

  const handleClearSelection = useCallback(() => {
    setSelectedFiles({ url: '', files: [] });
    setSelectedFile(null);
    setSelectedFileContent(null);
    fileContentCacheRef.current = {};
    saveToContext({ url, files: [], project_id: currentProject?.id ?? '' })
      .then(() => globalSnackbar({ message: 'Выбор очищен', severity: 'success' }))
      .catch((err) =>
        globalSnackbar({
          message: `Ошибка очистки: ${(err as Error).message}`,
          severity: 'error',
        })
      );
  }, [url, currentProject?.id]);

  const handleUrlBlur = useCallback(() => {
    setIsEditingUrl(false);
    try {
      localStorage.setItem(GIT_URL_KEY, url);
    } catch (_) {
      /* ignore */
    }
    if (url !== originalUrl) {
      setFileTree([]);
      setBranches([]);
      setSelectedFiles({ url: '', files: [] });
      setSelectedBranch('');
      setSelectedFile(null);
      setSelectedFileContent(null);
      fileContentCacheRef.current = {};
      indexedDBStorage.removeItem(FILES_KEY).catch(() => {
        /* ignore */
      });
      indexedDBStorage.removeItem(GIT_BRANCHES_CACHE_KEY).catch(() => {
        /* ignore */
      });
    }
  }, [url, originalUrl]);

  const handleBranchChange = useCallback((newBranch: string) => {
    setSelectedBranch(newBranch);
    setSelectedFile(null);
    setSelectedFileContent(null);
    try {
      localStorage.setItem(GIT_BRANCH_KEY, newBranch);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const getSelectedFileContent = useCallback(() => {
    if (!selectedFile || selectedFileContent === null) return null;
    return {
      fileName: selectedFile.name,
      language: getLanguageForFile(selectedFile.name),
      code: selectedFileContent ?? '',
    };
  }, [selectedFile, selectedFileContent]);

  return {
    url,
    setUrl,
    isAuthenticated,
    savedAccessToken,
    accessToken,
    setAccessToken,
    loginModalOpen,
    setLoginModalOpen,
    loading,
    loadingFiles,
    fileTree,
    setFileTree,
    branches,
    selectedBranch,
    setSelectedBranch,
    selectedFiles,
    setSelectedFiles,
    selectedFile,
    selectedFileContent,
    loadingFileContent,
    isEditingUrl,
    setIsEditingUrl,
    originalUrl,
    setOriginalUrl,
    isGitLoading,
    gitProgress,
    gitTaskId,
    fileContentCacheRef,
    expandedPaths,
    toggleExpandPath,
    handleLoadCode,
    handleLoadFiles,
    handleFileSelect,
    handleFileCheckboxSelect,
    handleLogin,
    handleLogout,
    handleCloseModal,
    handleCancelLoadFiles,
    handleCancelIndex,
    handleSaveToContext,
    handleClearSelection,
    handleUrlBlur,
    handleBranchChange,
    fetchFileContent,
    getSelectedFileContent,
    getLanguageForFile,
  };
}
